/* eslint-disable class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import { Container } from 'components/ui';
import ComponentList from 'components/Content/ComponentList';
import ChicletNav from 'components/Homepage/ChicletNav';
import ChicletNavExtended from 'components/Homepage/ChicletNavExtended';
import MediaUtils from 'utils/Media';
import SignInBanner from 'components/Homepage/SignInBanner';
import ContentConstants from 'constants/content';
import { screenReaderOnlyStyle } from 'style/config';
import p13nUtils from 'utils/localStorage/P13n';
import Location from 'utils/Location';
import SkeletonBanner from 'components/Banner/SkeletonBanner/SkeletonBanner';
import urlUtils from 'utils/Url';
import authenticationApi from 'services/api/authentication';
import userUtils from 'utils/User';
import localeUtils from 'utils/LanguageLocale';
import passkeysUtils from 'utils/passkeys';
import PasskeyOptinModalEN from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_en_US';
import PasskeyOptinModalFR from 'components/GlobalModals/PasskeyOptinModal/locales/PasskeyOptinModal_fr_CA';
import { PASSKEY_EXPIRED_ERROR_CODE } from 'constants/passkeys';

const { Media } = MediaUtils;
const { CONTEXTS } = ContentConstants;
const getText = localeUtils.getLocalisedDataFromResource(PasskeyOptinModalEN, PasskeyOptinModalFR);
const { isPasskeyEnabled } = passkeysUtils;

class Homepage extends BaseClass {
    hasShownPasskeyTestModal = false;
    hasHandledPasskeyLink = false;

    componentDidMount() {
        const cachedPersonalizationData = p13nUtils.getAllPersonalizedCache();

        if (cachedPersonalizationData) {
            this.props.setPersonalizationAnalyticsData(cachedPersonalizationData);
        }

        this.props.setPageLoadAnalytics();
        // Temporarily show consumer privacy modal on home page load if requested
        // Should be eliminated (the whole commit) when/if apps implement their logic internally

        if (this.props.showCCPADialog && !this.props.userAccountClosed) {
            this.props.showConsumerPrivacyModal({ isOpen: true });
        }

        if (this.props.userAccountClosed) {
            this.props.showCloseAccountSuccessfulModal(
                this.props.showCCPADialog ? () => this.props.showConsumerPrivacyModal({ isOpen: true }) : null
            );
        }

        this.handlePasskeyLink();
    }

    componentDidUpdate(prevProps) {
        const p13nAnalyticsData = digitalData.page.attributes.p13nAnalyticsData;

        if (prevProps.p13n !== this.props.p13n && this.props.p13n.headData && !p13nAnalyticsData) {
            this.props.setPersonalizationAnalyticsData(this.props.p13n.headData);
        }
    }

    handlePasskeyLink = async () => {
        if (userUtils.isSignedIn()) {
            return;
        }

        if (this.hasHandledPasskeyLink) {
            return;
        }

        const params = urlUtils.getParams(Location.getLocation().search, []);
        const securityToken = params.token && params.token[0];

        if (!securityToken) {
            return;
        }

        const location = Location.getLocation(true);

        // Skip if this is email verification route - let Footer handle it
        if (location.pathname.includes('/emailVerification') && !location.pathname.includes('/createPasskey')) {
            return;
        }

        const isSupported = await this.ensurePasskeySupported();

        if (!isSupported) {
            return;
        }

        try {
            // Guard against duplicate validate calls when componentDidMount chains handlers
            this.hasHandledPasskeyLink = true;
            const validationResponse = await authenticationApi.validatePasswordToken(securityToken, { pk: true });
            const credentialBindingLink = validationResponse?.credential_binding_link;
            const credentialBindingJob = validationResponse?.credential_binding_job;

            if (!credentialBindingLink) {
                Sephora.logger?.warn('Missing credential_binding_link');

                return;
            }

            this.props.showPasskeyOptinModal({
                isOpen: true,
                passkeyType: 'optinCreateAccount',
                credentialBindingLink,
                credentialBindingJob,
                securityToken
            });
        } catch (error) {
            const errorCode = error?.errors?.[0]?.errorCode;
            const message = errorCode === PASSKEY_EXPIRED_ERROR_CODE ? getText('passkeyExpiredLinkMessage') : getText('passkeyErrorMessage');

            if (message) {
                this.showPasskeyErrorModal(message);
            }
        }
    };

    componentWillUnmount() {
        this.props.setP13NInitialization(false);
    }

    showPasskeyErrorModal = message => {
        this.props.showInfoModal({
            isOpen: true,
            title: getText('passkeyErrorTitle'),
            message,
            buttonText: 'OK',
            bodyPaddingBottom: 4,
            isHtml: true
        });
    };

    ensurePasskeySupported = async () => {
        if (!isPasskeyEnabled()) {
            Location.navigateTo(null, urlUtils.getLink('/'));

            return false;
        }

        const isSupported = await passkeysUtils.isPasskeySupported();

        if (!isSupported) {
            this.showPasskeyErrorModal(getText('passkeyUnsupportedDeviceMessage'));
        }

        return isSupported;
    };

    isInitializedAndNoData() {
        const { items, p13n } = this.props;

        return p13n.isInitialized && !items.length;
    }

    getDynamicTrackingCount() {
        const { items } = this.props;

        // Find the position of the BannerList (carousel) component
        const bannerListIndex = items.findIndex(item => item.type === 'BannerList');

        // If BannerList is found, return its position + 1 (to include it in tracking)
        // If not found, default to 1 (original behavior)
        return bannerListIndex >= 0 ? bannerListIndex + 1 : 1;
    }

    render() {
        const {
            headerText, p13n, items, seoJSON, schemas
        } = this.props;

        // Calculate dynamic tracking count based on BannerList position
        const dynamicTrackingCount = this.getDynamicTrackingCount();

        return (
            <>
                <h1
                    css={screenReaderOnlyStyle}
                >
                    {headerText}
                </h1>
                <Media at='xs'>
                    <SignInBanner />
                </Media>
                <Media lessThan='md'>
                    {/* Skip skeleton for SEO - render content immediately */}
                    {!Sephora.isSEO && !p13n.isInitialized && !p13n.headData ? (
                        <SkeletonBanner />
                    ) : (
                        Location.isHomepage() && !!items.length && <ChicletNav />
                    )}
                </Media>
                <Container>
                    {this.isInitializedAndNoData() ? (
                        <ChicletNavExtended />
                    ) : (
                        <ComponentList
                            enablePageRenderTracking={true}
                            trackingCount={dynamicTrackingCount}
                            context={CONTEXTS.CONTAINER}
                            page='home'
                            items={items}
                            removeLastItemMargin={true}
                        />
                    )}
                </Container>
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(seoJSON)
                    }}
                ></script>
                {schemas?.length &&
                    schemas.map(schema => (
                        <script
                            key={schema['@type']}
                            type='application/ld+json'
                            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
                        ></script>
                    ))}
            </>
        );
    }
}

Homepage.propTypes = {
    headerText: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
    seoJSON: PropTypes.object.isRequired,
    p13n: PropTypes.object.isRequired,
    user: PropTypes.object,
    setP13NInitialization: PropTypes.func.isRequired,
    setPageLoadAnalytics: PropTypes.func.isRequired,
    showCCPADialog: PropTypes.bool.isRequired,
    userAccountClosed: PropTypes.bool,
    showConsumerPrivacyModal: PropTypes.func.isRequired,
    showCloseAccountSuccessfulModal: PropTypes.func.isRequired,
    showPasskeyOptinModal: PropTypes.func.isRequired,
    showInfoModal: PropTypes.func.isRequired,
    setPersonalizationAnalyticsData: PropTypes.func.isRequired,
    schemas: PropTypes.array
};

export default wrapComponent(Homepage, 'Homepage', true);
